import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Save, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isSuccess = (response) => response === true || response === 'true' || response === 'Success';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const ChangeStoreRef = () => {
  const [areas, setAreas] = useState([]);
  const [orders, setOrders] = useState([]);
  const [areaId, setAreaId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const editedCount = useMemo(() => orders.filter((order) => String(order.StoreRefNoNew ?? '').trim() !== '').length, [orders]);

  const loadAreas = useCallback(async () => {
    try {
      const response = await MainService.AreasInDropDown();
      setAreas(parseList(response));
    } catch (err) {
      console.error('Load areas error:', err);
      setAreas([]);
      setError('Failed to load areas');
    }
  }, []);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const filterOrders = async () => {
    if (!areaId) {
      setError('Select Area');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetReturnedOrdersByAreaId({ AreaId: Number(areaId) });
      setOrders(parseList(response).map((order) => ({ ...order, StoreRefNoNew: order.StoreRefNoNew || '' })));
    } catch (err) {
      console.error('Filter returned orders error:', err);
      setOrders([]);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const updateStoreRefDraft = (orderId, value) => {
    setOrders((prev) => prev.map((order) => (order.Id === orderId ? { ...order, StoreRefNoNew: value } : order)));
  };

  const saveOrders = async () => {
    if (orders.length === 0) {
      setError('No orders to save');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = orders.map((order) => ({
        ...order,
        StoreRefNoNew: Number(order.StoreRefNoNew) || 0,
      }));
      const response = await MainService.SaveStoreRefReturnOrders(payload);
      if (isSuccess(response)) {
        setSuccess('Success');
        setAreaId('');
        setOrders([]);
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Save returned order store refs error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const refresh = () => {
    setAreaId('');
    setOrders([]);
    loadAreas();
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Change Store Ref</h1>
            <p className="mt-1 text-sm text-gray-500">Filter returned orders by area and assign new store reference numbers.</p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Area</label>
            <select
              value={areaId}
              onChange={(event) => setAreaId(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select Area</option>
              {areas.map((area) => (
                <option key={area.Id} value={area.Id}>
                  {area.Name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 lg:col-span-8">
            <button
              type="button"
              onClick={filterOrders}
              disabled={loading || saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              Filter
            </button>
            <button
              type="button"
              onClick={saveOrders}
              disabled={saving || orders.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save New Numbers
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Returned Orders</h2>
            <p className="text-sm text-gray-500">
              {orders.length} orders found, {editedCount} new numbers entered
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1400px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Voucher No</Th>
                <Th>Delivery Date</Th>
                <Th>Client</Th>
                <Th>Client Add</Th>
                <Th>Customer</Th>
                <Th>Customer Add</Th>
                <Th>Pro Price</Th>
                <Th>Ship Charge</Th>
                <Th>Tax</Th>
                <Th>Delivery Charge</Th>
                <Th>Pay Type</Th>
                <Th>Atps</Th>
                <Th>Status</Th>
                <Th>Store Ref No</Th>
                <Th>Store Ref New</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={16} text="Loading orders..." />
              ) : orders.length === 0 ? (
                <EmptyRow colSpan={16} text="No returned orders found." />
              ) : (
                orders.map((order, index) => (
                  <tr key={order.Id} className="hover:bg-gray-50">
                    <Td>{index + 1}</Td>
                    <Td strong>{order.VoucherNo}</Td>
                    <Td>{formatDate(order.DeliveryDate)}</Td>
                    <Td>
                      <div>{order.ClientName || '-'}</div>
                      <div className="text-xs text-gray-500">{order.ClientPhone || ''}</div>
                    </Td>
                    <Td>{[order.ClEmitare, order.ClLoc].filter(Boolean).join(', ') || '-'}</Td>
                    <Td>
                      <div>{order.CustomerName || '-'}</div>
                      <div className="text-xs text-gray-500">{order.CustomerPhone || ''}</div>
                    </Td>
                    <Td>{[order.CustEmirate, order.CustLoc].filter(Boolean).join(', ') || '-'}</Td>
                    <Td>{formatAmount(order.ProductPrice)}</Td>
                    <Td>{formatAmount(order.ServiceCharge)}</Td>
                    <Td>{formatAmount(order.TAX)}</Td>
                    <Td>{formatAmount(order.DeliveryCharge)}</Td>
                    <Td>{String(order.PayType) === '1' ? 'Customer' : 'Client'}</Td>
                    <Td>{order.Attempts ?? 0}</Td>
                    <Td>{order.VoucherStatus || '-'}</Td>
                    <Td>{order.StoreRefNo ?? 0}</Td>
                    <Td>
                      <input
                        type="number"
                        value={order.StoreRefNoNew ?? ''}
                        onChange={(event) => updateStoreRefDraft(order.Id, event.target.value)}
                        className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children }) => (
  <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
    {children}
  </th>
);

const Td = ({ children, strong = false }) => (
  <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
    {children}
  </td>
);

const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">
      {text}
    </td>
  </tr>
);

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default ChangeStoreRef;
